import React, { useMemo, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Link, Snackbar, TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  outcomesApi, locationEquivalenciesApi, OutcomesRecommendation,
  RecommendationDirection,
} from '../api/services';
import { extractApiError } from '../utils/apiErrors';
import { formatCents, formatDate } from '../utils/format';
import { CombinedDecisionTable, isDead } from '../components/outcomes/CombinedDecisionTable';
import { LocationInventoryChart } from '../components/outcomes/LocationInventoryChart';
import { DigestOutcomes } from '../components/outcomes/DigestOutcomes';

// Per-location check-in detail, reworked around ONE combined decision table
// (sc-6127): the location chart first (big picture, with the if-recs-applied
// ceiling), then the combined table — recommendations, costs, and the
// day-binned digest/order pattern together, with row states replacing the old
// keeps/suppressed/monitoring/recent-terminal side lists and the row drawer —
// then the digest-outcomes audit, unchanged at the bottom. Apply still goes
// through the EXISTING LPE upsert endpoint; nothing here mutates inventory
// through the Outcomes API.

type ApplyTarget = {
  productEquivalencyId: number;
  name: string;
  currentMax: number | null;
  direction?: RecommendationDirection;
  recId?: number; // the lifecycle rec being applied
  isDeadRetire?: boolean; // a DEAD rec — 0 is the intended retire value
  value: string;
  // sc-5986: preferred SKU's box size for boxed groups — the dialog labels the
  // field as total units and shows the whole-box arithmetic of the prefill.
  hqUnitsPerBox?: number | null;
  recBoxes?: number | null;
  busy?: boolean;
  error?: string;
};

const EdgeReviewLocationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: review, isLoading, error } = useQuery({
    queryKey: ['outcomes-review', id],
    queryFn: () => outcomesApi.review(id!),
    enabled: !!id,
    staleTime: 30000,
  });

  const [applyDialog, setApplyDialog] = useState<ApplyTarget | null>(null);
  const [declineDialog, setDeclineDialog] = useState<{ rec: OutcomesRecommendation; note: string; busy?: boolean; error?: string } | null>(null);
  const [appliedIds, setAppliedIds] = useState<Set<number>>(new Set());
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [regenConfirm, setRegenConfirm] = useState(false);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['outcomes-review', id] });
    queryClient.invalidateQueries({ queryKey: ['outcomes-locations'] });
    // Apply/Decline move the live max/rec references — the if-recs ceiling
    // (inventory chart) and the per-group drawdown lines must follow without
    // a page reload (sc-6127; both cache with a 5-minute staleTime).
    queryClient.invalidateQueries({ queryKey: ['outcomes', 'inventory-chart'] });
    queryClient.invalidateQueries({ queryKey: ['outcomes', 'group-chart'] });
  };

  // On-demand regeneration: force a fresh top-down review for this
  // location. The endpoint returns the fresh review, so seed the query cache
  // directly (no refetch); invalidate the index summary it also changed.
  const regenerate = useMutation({
    mutationFn: () => outcomesApi.regenerate(id!),
    onSuccess: (fresh) => {
      queryClient.setQueryData(['outcomes-review', id], fresh);
      queryClient.invalidateQueries({ queryKey: ['outcomes-locations'] });
      // The regen recomputed recs — refresh the if-recs ceiling and any
      // cached drawdown reference lines along with it.
      queryClient.invalidateQueries({ queryKey: ['outcomes', 'inventory-chart'] });
      queryClient.invalidateQueries({ queryKey: ['outcomes', 'group-chart'] });
      setRegenConfirm(false);
      setSnackbar({ severity: 'success', message: 'Report regenerated.' });
    },
    onError: (e) => {
      setRegenConfirm(false);
      setSnackbar({ severity: 'error', message: extractApiError(e, 'Regenerate failed') });
    },
  });

  // Generate the customer-facing PDF: fetch the bytes (token-authenticated blob,
  // sc-5916), then trigger an object-URL download with the server filename.
  const generatePdf = useMutation({
    mutationFn: () => outcomesApi.reportPdf(id!),
    onSuccess: ({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Defer revoke: revoking synchronously right after click can cancel the
      // in-flight download on some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 0);
      setSnackbar({ severity: 'success', message: 'PDF downloaded.' });
    },
    // The request is responseType:'blob', so on error the server's JSON {error}
    // arrives as a Blob — read it back before falling through to the generic message.
    onError: async (e) => {
      let message = 'PDF generation failed';
      const data = (e as { response?: { data?: unknown } })?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          message = parsed.error ?? parsed.errors?.[0]?.detail ?? message;
        } catch { /* keep default */ }
      } else {
        message = extractApiError(e, message);
      }
      setSnackbar({ severity: 'error', message });
    },
  });

  const summaryLine = useMemo(() => {
    if (!review) return '';
    const directions = { set: 0, raise: 0, lower: 0 };
    review.recommendations.forEach((rec) => { directions[rec.direction] += 1; });
    const parts = [
      directions.raise > 0 && `${directions.raise} raise`,
      directions.set > 0 && `${directions.set} set`,
      directions.lower > 0 && `${directions.lower} lower`,
      review.keeps.length > 0 && `${review.keeps.length} keep`,
      review.suppressed.length > 0 && `${review.suppressed.length} quiet`,
      review.still_monitoring.length > 0 && `${review.still_monitoring.length} still monitoring`,
    ].filter(Boolean);
    return parts.join(' · ');
  }, [review]);

  // Allow 0 only for a DEAD retire (the de-stock action, sc-5875). For any other
  // target a 0 (incl. an accidentally-cleared field) would silently set "no max"
  // on a live bin — block it and point at Decline. Re-applying the current max is
  // also a no-op the sweep can't act on.
  const applyValidationError = (target: ApplyTarget, value: number): string | null => {
    if (!Number.isInteger(value) || value < 0) return 'Enter a non-negative whole number';
    if (value === 0 && !target.isDeadRetire) {
      return "A max of 0 means don't stock — only for dead stock. To skip this recommendation, use Decline instead.";
    }
    if (target.recId != null && target.currentMax != null && value === target.currentMax) {
      return `The max is already ${target.currentMax} — to leave it unchanged, use Decline instead.`;
    }
    return null;
  };

  const satisfiesDirection = (target: ApplyTarget, value: number): boolean => {
    if (!target.direction) return true; // no direction (defensive) — any level is the rep's call
    if (target.direction === 'set') return value >= 1;
    if (target.currentMax == null) return true;
    return target.direction === 'raise' ? value > target.currentMax : value < target.currentMax;
  };

  const openApply = (rec: OutcomesRecommendation) => {
    setApplyDialog({
      productEquivalencyId: rec.group.product_equivalency_id,
      name: rec.group.name ?? 'group',
      currentMax: rec.current_max,
      direction: rec.direction,
      recId: rec.id,
      isDeadRetire: isDead(rec),
      value: String(rec.recommended_value),
      hqUnitsPerBox: rec.hq_units_per_box,
      recBoxes: rec.rec_boxes,
    });
  };

  const handleApply = async () => {
    if (!applyDialog || !review) return;
    const parsed = Number(applyDialog.value);
    const validationError = applyValidationError(applyDialog, parsed);
    if (validationError) {
      setApplyDialog((d) => d && { ...d, error: validationError });
      return;
    }
    setApplyDialog((d) => d && { ...d, busy: true, error: undefined });
    try {
      await locationEquivalenciesApi.upsert({
        location_id: review.location_id,
        product_equivalency_id: applyDialog.productEquivalencyId,
        max_count: parsed,
        status: 'active',
      });
      if (applyDialog.recId != null) setAppliedIds((prev) => new Set(prev).add(applyDialog.recId!));
      let message: string;
      if (applyDialog.recId == null) {
        message = `Max set to ${parsed} for ${applyDialog.name}.`;
      } else if (applyDialog.isDeadRetire && applyDialog.currentMax == null) {
        // No prior max → applying 0 fires no max event, so it completes on the
        // next cycle reconcile (when the review recomputes as a keep), not the
        // nightly sweep (sc-5916).
        message = `Max set to 0 for ${applyDialog.name} — don't stock. Completes on the next review cycle.`;
      } else if (satisfiesDirection(applyDialog, parsed)) {
        message = `Max set to ${parsed} for ${applyDialog.name}. The recommendation completes on tonight's sweep.`;
      } else {
        message = `Max set to ${parsed} for ${applyDialog.name}. That's outside the recommended direction — the recommendation re-evaluates against the new max on tonight's sweep.`;
      }
      setSnackbar({ severity: 'success', message });
      setApplyDialog(null);
      invalidate();
    } catch (e) {
      setApplyDialog((d) => d && { ...d, busy: false, error: extractApiError(e, 'Applying the max failed') });
    }
  };

  const handleDecline = async () => {
    if (!declineDialog) return;
    if (!declineDialog.note.trim()) {
      setDeclineDialog((d) => d && { ...d, error: 'A note is required — it suppresses re-recommendation for 4 cycles' });
      return;
    }
    setDeclineDialog((d) => d && { ...d, busy: true, error: undefined });
    try {
      await outcomesApi.decline(declineDialog.rec.id, declineDialog.note.trim());
      setSnackbar({ severity: 'success', message: `Declined ${declineDialog.rec.group.name}.` });
      setDeclineDialog(null);
      invalidate();
    } catch (e) {
      setDeclineDialog((d) => d && { ...d, busy: false, error: extractApiError(e, 'Decline failed') });
    }
  };

  if (isLoading) {
    return <Box sx={{ p: 4, display: 'flex', gap: 2 }}><CircularProgress size={22} /><Typography>Loading review…</Typography></Box>;
  }
  if (error || !review) {
    return <Box sx={{ p: 4 }}><Alert severity="error">{extractApiError(error, 'Could not load the review')}</Alert></Box>;
  }

  const orderPattern = review.order_pattern;
  const orderTotals = orderPattern?.account_linked && orderPattern.order_data_available
    ? orderPattern.totals
    : null;

  return (
    <Box sx={{ p: 2 }}>
      <Link component={RouterLink} to="/outcomes" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
        <ArrowBackIcon fontSize="small" /> Edge Reviews
      </Link>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4">{review.name} — Edge review</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {/* Generate PDF needs a review to render; disabled until the first run. */}
          <Button variant="outlined" onClick={() => generatePdf.mutate()} disabled={generatePdf.isPending || !review.reviewed}>
            {generatePdf.isPending ? 'Generating…' : 'Generate PDF'}
          </Button>
          {/* Regenerate is available for every monitored location, including those
              awaiting their first review — this is the path to the first report. */}
          <Button variant="outlined" onClick={() => setRegenConfirm(true)} disabled={regenerate.isPending}>
            {regenerate.isPending ? 'Regenerating…' : 'Regenerate report'}
          </Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {review.reviewed
          ? <>Reviewed {formatDate(review.run_on)}{summaryLine && <> · {summaryLine}</>}</>
          : 'Awaiting first review run — this location enters the 6-week cycle on its assigned slot.'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        <Chip
          size="small"
          variant="outlined"
          label={review.uses_stock_truck
            ? `Stock truck route · ${review.protection_days}-day demand window`
            : `Ad-hoc replenishment · ${review.protection_days}-day demand window`}
        />
        {review.dead_stock && (review.dead_stock.value_cents > 0 || (review.dead_stock.unpriced_units ?? 0) > 0) && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={(() => {
              const unpriced = review.dead_stock.unpriced_units ?? 0;
              const unitsSuffix = `${unpriced} unit${unpriced === 1 ? '' : 's'}`;
              if (review.dead_stock.value_cents > 0) {
                return `Dead stock: ${formatCents(review.dead_stock.value_cents)} capital tied up${unpriced > 0 ? ` · +${unitsSuffix} unpriced` : ''}`;
              }
              return `Dead stock: ${unitsSuffix} (price unavailable)`;
            })()}
          />
        )}
        {/* Expedited premium (sc-6127): the total spend on expediting outright
            — billed freight × per-class premium rate — over the trailing
            window, read-time. Rush counts ride here now (they used to live on
            the avoidable chip). */}
        {orderTotals && orderTotals.expedited_order_count > 0 && (
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`Expedited: ${formatCents(orderTotals.premium_cents)} premium · ${orderTotals.expedited_order_count} of ${orderTotals.order_count} orders · last 6 months`}
          />
        )}
        {/* Avoidable-with-higher-max keeps the existing counterfactual measure;
            shown alongside the expedited chip so the contrast reads (a $0 here
            with a big expedited number = expediting from ignored digests, not
            low maxes). */}
        {review.avoidable_expediting_cost && (review.avoidable_expediting_cost.value_cents > 0 || (orderTotals?.expedited_order_count ?? 0) > 0) && (
          <Chip
            size="small"
            color="info"
            variant="outlined"
            label={`Avoidable with higher max: ${formatCents(review.avoidable_expediting_cost.value_cents)}`}
          />
        )}
      </Box>

      {/* Location HVA roll-up chart (sc-6057) — first on the page (sc-6127),
          with the if-recs-applied ceiling. */}
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Inventory & HVA history — since go-live</Typography>
      <LocationInventoryChart locationId={review.location_id} />

      {/* The combined decision table (sc-6127). */}
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Stocking levels & order pattern</Typography>
      <CombinedDecisionTable
        review={review}
        appliedIds={appliedIds}
        onApply={openApply}
        onDecline={(rec) => setDeclineDialog({ rec, note: '' })}
      />

      {/* Digest Outcomes acceptance mix (sc-6064) — unchanged, stays at the bottom. */}
      <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Digest outcomes — restock acceptance</Typography>
      <DigestOutcomes locationId={review.location_id} />

      {/* Apply dialog */}
      <Dialog open={!!applyDialog} onClose={() => !applyDialog?.busy && setApplyDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Set max — {applyDialog?.name}</DialogTitle>
        <DialogContent>
          {applyDialog?.error && <Alert severity="error" sx={{ mb: 2 }}>{applyDialog.error}</Alert>}
          <DialogContentText sx={{ mb: 1 }}>
            Sets the group max at {review.name}. Prefilled with the recommended level — adjust to the
            customer's agreed number (recorded as an override). A max of 0 means don't stock the group.
            {/* sc-5986: boxed groups — the max is total units; surface the
                whole-box arithmetic behind the prefill (derived from the rec,
                not the live field, so edits can't relabel it). recBoxes > 0
                also suppresses it for DEAD retires — "0 units = 0 boxes"
                adds nothing next to "A max of 0 means don't stock". */}
            {applyDialog?.hqUnitsPerBox != null && applyDialog?.recBoxes != null && applyDialog.recBoxes > 0 && (
              <> Recommended: {applyDialog.recBoxes * applyDialog.hqUnitsPerBox} units = {applyDialog.recBoxes} box{applyDialog.recBoxes === 1 ? '' : 'es'} of {applyDialog.hqUnitsPerBox}.</>
            )}
          </DialogContentText>
          <TextField
            autoFocus fullWidth type="number" margin="dense"
            label="Max (total units)"
            value={applyDialog?.value ?? ''}
            onChange={(e) => setApplyDialog((d) => d && { ...d, value: e.target.value })}
            disabled={applyDialog?.busy}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApplyDialog(null)} disabled={applyDialog?.busy}>Cancel</Button>
          <Button onClick={handleApply} variant="contained" disabled={applyDialog?.busy}>
            {applyDialog?.busy ? 'Applying…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decline dialog */}
      <Dialog open={!!declineDialog} onClose={() => !declineDialog?.busy && setDeclineDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Decline — {declineDialog?.rec.group.name}</DialogTitle>
        <DialogContent>
          {declineDialog?.error && <Alert severity="error" sx={{ mb: 2 }}>{declineDialog.error}</Alert>}
          <DialogContentText sx={{ mb: 1 }}>
            Declining suppresses this direction for the next 4 review cycles. A note is required.
          </DialogContentText>
          <TextField
            autoFocus fullWidth multiline minRows={2} label="Why is this declined?" margin="dense"
            value={declineDialog?.note ?? ''}
            onChange={(e) => setDeclineDialog((d) => d && { ...d, note: e.target.value })}
            disabled={declineDialog?.busy}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeclineDialog(null)} disabled={declineDialog?.busy}>Cancel</Button>
          <Button onClick={handleDecline} variant="contained" color="warning" disabled={declineDialog?.busy}>
            {declineDialog?.busy ? 'Declining…' : 'Decline'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate confirm dialog — framed as a heavy operation */}
      <Dialog open={regenConfirm} onClose={() => !regenerate.isPending && setRegenConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Regenerate report — {review.name}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This recomputes the entire review for {review.name} from the latest inventory and order
            data, and reconciles its open recommendations. It's a heavy operation and may take a few
            seconds. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegenConfirm(false)} disabled={regenerate.isPending}>Cancel</Button>
          <Button onClick={() => regenerate.mutate()} variant="contained" disabled={regenerate.isPending}>
            {regenerate.isPending ? 'Regenerating…' : 'Regenerate'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar} autoHideDuration={6000} onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity || 'success'} onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EdgeReviewLocationPage;
